import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, TrendingUp, XCircle, SkipForward, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const RevisionEffectivenessGauge = ({ revisionAnalysis }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [frequencyFilter, setFrequencyFilter] = useState('all');
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  // Extract data (with defaults to avoid errors)
  const { 
    totalPracticeSessions = 0, 
    totalQuestionsRevised = 0, 
    totalWrongQuestions = 0,
    totalSkippedQuestions = 0,
    revisionEffectiveness = 0, 
    mostRevisedTopics = [], 
    revisionConsistency = 0,
    questionsWithAttempts = []
  } = revisionAnalysis || {};

  // Get wrong questions with attempt counts (must be before early return)
  const wrongQuestionsWithAttempts = useMemo(() => {
    if (!questionsWithAttempts || questionsWithAttempts.length === 0) return [];
    
    return questionsWithAttempts
      .map(q => ({
        id: q.id || q.questionId,
        topic: q.topic || q.subject || 'General',
        type: (q.status === 'wrong' || q.isWrong) ? 'wrong' : 'skipped',
        attempts: q.attempts || 1,
        question: q.questionText || q.question || 'Question text not available',
        options: q.options || ['Option A', 'Option B', 'Option C', 'Option D'],
        correctAnswer: q.correctAnswer || q.answer || 'Not available',
        wrongCount: q.wrongCount || 0,
        skippedCount: q.skippedCount || 0
      }))
      .sort((a, b) => b.attempts - a.attempts);
  }, [questionsWithAttempts]);

  // Filter questions by frequency (must be before early return)
  const filteredQuestions = useMemo(() => {
    if (frequencyFilter === 'all') return wrongQuestionsWithAttempts;
    if (frequencyFilter === '2+') return wrongQuestionsWithAttempts.filter(q => q.attempts >= 2);
    if (frequencyFilter === '5+') return wrongQuestionsWithAttempts.filter(q => q.attempts >= 5);
    if (frequencyFilter === '10+') return wrongQuestionsWithAttempts.filter(q => q.attempts >= 10);
    return wrongQuestionsWithAttempts;
  }, [wrongQuestionsWithAttempts, frequencyFilter]);

  // Early return check (after all hooks)
  if (!revisionAnalysis || (totalPracticeSessions === 0 && totalWrongQuestions === 0 && totalSkippedQuestions === 0)) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
      >
        <p className="text-gray-500 dark:text-gray-400">No revision data available</p>
      </motion.div>
    );
  }

  const getEffectivenessColor = (score) => {
    if (score >= 80) return { color: 'text-green-600', bg: 'from-green-400 to-green-600' };
    if (score >= 60) return { color: 'text-blue-600', bg: 'from-blue-400 to-blue-600' };
    if (score >= 40) return { color: 'text-yellow-600', bg: 'from-yellow-400 to-yellow-600' };
    return { color: 'text-red-600', bg: 'from-red-400 to-red-600' };
  };

  const effectivenessStyle = getEffectivenessColor(revisionEffectiveness);
  const circumference = 2 * Math.PI * 70;
  const strokeDashoffset = circumference - (revisionEffectiveness / 100) * circumference;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-teal-600 rounded-lg">
            <RefreshCw className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Revision Effectiveness</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{totalPracticeSessions} practice sessions</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'overview'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('questions')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'questions'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Questions ({filteredQuestions.length})
        </button>
      </div>

      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {/* Circular Gauge */}
            {totalPracticeSessions > 0 ? (
              <div className="flex items-center justify-center mb-6">
                <div className="relative">
                  <svg width="180" height="180" className="transform -rotate-90">
                    {/* Background circle */}
                    <circle
                      cx="90"
                      cy="90"
                      r="70"
                      stroke="#e5e7eb"
                      strokeWidth="12"
                      fill="none"
                    />
                    {/* Progress circle */}
                    <motion.circle
                      cx="90"
                      cy="90"
                      r="70"
                      stroke="url(#gradient)"
                      strokeWidth="12"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      initial={{ strokeDashoffset: circumference }}
                      animate={{ strokeDashoffset }}
                      transition={{ duration: 1.5, ease: 'easeOut' }}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" className="text-teal-400" stopColor="currentColor" />
                        <stop offset="100%" className="text-teal-600" stopColor="currentColor" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <motion.p
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.5, type: 'spring' }}
                        className={`text-4xl font-bold ${effectivenessStyle.color}`}
                      >
                        {revisionEffectiveness}%
                      </motion.p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Effectiveness</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center mb-6 py-8">
                <div className="text-center">
                  <AlertTriangle className="h-16 w-16 mx-auto mb-3 text-yellow-500 opacity-50" />
                  <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    No Practice Sessions Yet
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Start practicing wrong and skipped questions to track your revision progress
                  </p>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20 rounded-lg p-4 text-center"
              >
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Questions Revised</p>
                <p className="text-2xl font-bold text-teal-600">{totalQuestionsRevised}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.25 }}
                className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-lg p-4 text-center"
              >
                <div className="flex items-center justify-center gap-1 mb-1">
                  <XCircle className="h-3 w-3 text-red-600" />
                  <p className="text-xs text-gray-600 dark:text-gray-400">Wrong</p>
                </div>
                <p className="text-2xl font-bold text-red-600">{totalWrongQuestions}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 rounded-lg p-4 text-center"
              >
                <div className="flex items-center justify-center gap-1 mb-1">
                  <SkipForward className="h-3 w-3 text-yellow-600" />
                  <p className="text-xs text-gray-600 dark:text-gray-400">Skipped</p>
                </div>
                <p className="text-2xl font-bold text-yellow-600">{totalSkippedQuestions}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.35 }}
                className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 text-center"
              >
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Consistency</p>
                <p className="text-2xl font-bold text-blue-600">{revisionConsistency}%</p>
              </motion.div>
            </div>

            {/* Most Revised Topics */}
            {mostRevisedTopics && mostRevisedTopics.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-teal-600" />
                  Most Revised Topics
                </h4>
                <div className="space-y-2">
                  {mostRevisedTopics.slice(0, 3).map((topic, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{topic.topic}</span>
                        <div className="flex items-center gap-3 mt-1">
                          {topic.revisedCount > 0 && (
                            <span className="text-xs text-teal-600 dark:text-teal-400 font-medium">
                              ✓ {topic.revisedCount} revised
                            </span>
                          )}
                          {topic.wrongCount > 0 && (
                            <span className="text-xs text-red-600 flex items-center gap-1">
                              <XCircle className="h-3 w-3" />
                              {topic.wrongCount} wrong
                            </span>
                          )}
                          {topic.skippedCount > 0 && (
                            <span className="text-xs text-yellow-600 flex items-center gap-1">
                              <SkipForward className="h-3 w-3" />
                              {topic.skippedCount} skipped
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs font-bold text-teal-600">+{topic.improvementRate}%</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {activeTab === 'questions' && (
          <motion.div
            key="questions"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {/* Frequency Filter */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {['all', '2+', '5+', '10+'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setFrequencyFilter(filter)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    frequencyFilter === filter
                      ? 'bg-teal-600 text-white shadow-lg'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {filter === 'all' ? 'All Questions' : `${filter} Attempts`}
                  <span className="ml-2 text-xs opacity-75">
                    ({filter === 'all' 
                      ? wrongQuestionsWithAttempts.length 
                      : wrongQuestionsWithAttempts.filter(q => {
                          if (filter === '2+') return q.attempts >= 2;
                          if (filter === '5+') return q.attempts >= 5;
                          if (filter === '10+') return q.attempts >= 10;
                          return true;
                        }).length
                    })
                  </span>
                </button>
              ))}
            </div>

            {/* Questions List */}
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {filteredQuestions.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No questions found with {frequencyFilter} attempts</p>
                </div>
              ) : (
                filteredQuestions.map((question, index) => (
                  <motion.div
                    key={question.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border-l-4 border-l-teal-600"
                  >
                    {/* Question Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            question.type === 'wrong' 
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          }`}>
                            {question.type === 'wrong' ? 'Wrong' : 'Skipped'}
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {question.topic}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {question.question}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-full text-xs font-bold">
                          {question.attempts}x
                        </span>
                        <button
                          onClick={() => setExpandedQuestion(expandedQuestion === question.id ? null : question.id)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                        >
                          {expandedQuestion === question.id ? (
                            <ChevronUp className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Options */}
                    <AnimatePresence>
                      {expandedQuestion === question.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600"
                        >
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Options:</p>
                          <div className="space-y-2">
                            {question.options.map((option, idx) => (
                              <div
                                key={idx}
                                className={`p-2 rounded text-sm ${
                                  option === question.correctAnswer
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 font-medium'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {option}
                                {option === question.correctAnswer && (
                                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">✓ Correct</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default RevisionEffectivenessGauge;
